// Grooming 시트 (docs/ux/draft-v3): 시트 안 미리보기 칸(배경 없이)에서 내 카피바라가 소품을 입어 본다.
// 무료 소품은 고르면 바로 입고, 유료 소품은 입어 보기만 (Get it → Leaf 상점). 미리보기는 따로 작은 렌더러로 그린다.
import * as THREE from 'three';
import { ITEMS, type HandItemId, type HeadItemId, type ItemId, type RoomId } from '@capy/shared';
import { LEGACY } from '../engine/lights';
import { makeAgent, setFur, setSize, type Capy } from '../capy/agent';
import { setFace, setHand, setHead, type FaceKey } from '../capy/equip';
import { pose, type Room } from '../capy/pose';
import { FUR, type FurTone } from '../capy/model';
import { ITEM_TEXT, HUD_TEXT, fill } from '../text/en';
import { LINE } from './lineIcons';
import { el, iconBtn } from './shell';

type GTab = 'head' | 'face' | 'hand' | 'size' | 'fur';
/** 미리보기·착용 상태 (한 칸씩). */
interface Look { head: HeadItemId | 'none'; face: FaceKey; hand: HandItemId | 'none'; size: 'normal' | 'baby'; fur: FurTone }

export interface GroomingOpts {
  room(): Room;
  player: Capy;
  /** 지금 입은 것. */
  look(): Look;
  /** 무료(가진) 소품을 내 카피바라에 입힌다. */
  wear(slot: GTab, key: string): void;
  onClose(): void;
  /** 유료 소품 "Get it" (Leaf 상점 열기). */
  onGet(key: ItemId, leaf: number): void;
}

export interface Grooming { readonly el: HTMLElement; readonly isOpen: boolean; open(): void; close(): void; update(t: number, dt: number): void }

const TX = HUD_TEXT.groom;
const FALLBACK_ICON: Partial<Record<ItemId, string>> = { bagaji: '🥣', scrubmitt: '🧽', scratcher: '🪵' };
const iconOf = (k: ItemId) => { const m = /^(\p{Extended_Pictographic}[\u{FE0F}\u{200D}\p{Extended_Pictographic}]*)\s/u.exec(ITEM_TEXT[k].label); return m ? m[1]! : FALLBACK_ICON[k] ?? '•'; };
const nameOf = (k: ItemId) => ITEM_TEXT[k].name ?? ITEM_TEXT[k].label.replace(/^\S+\s/u, '');

export function createGrooming(o: GroomingOpts): Grooming {
  const box = el('div', 'sheet glass groom');
  box.append(el('div', 'grab'));
  const head = el('div', 'phead');
  head.append(el('h3', '', TX.title));
  const x = iconBtn('x', LINE.close, undefined, 'Close');
  head.append(x);
  const prevBox = el('div', 'preview');
  const canvas = el('canvas');
  const tag = el('span', 'tag', TX.tryingOn);
  prevBox.append(canvas, tag);
  const tabsEl = el('div', 'gtabs');
  const grid = el('div', 'items');
  const foot = el('div', 'gfoot');
  box.append(head, prevBox, tabsEl, grid, foot);

  let open = false, tab: GTab = 'head', trying: Look | null = null;
  let renderer: THREE.WebGLRenderer | null = null, scene: THREE.Scene, camera: THREE.PerspectiveCamera, prev: Capy;
  let spin = 0.25, dragX: number | null = null;

  function ensureRenderer() {
    if (renderer) return;
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0xEFE6D8, 1);
    scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xFFFFFF, 0xD8C8B0, 0.62 * LEGACY));
    const k = new THREE.DirectionalLight(0xFFF1DE, 0.5 * LEGACY); k.position.set(3, 8, 6); scene.add(k);
    camera = new THREE.PerspectiveCamera(30, 1.6, 0.1, 60);
    prev = makeAgent(scene, false, o.look().fur);
    prev.ripple.visible = false;
    canvas.addEventListener('pointerdown', (e) => { dragX = e.clientX; canvas.setPointerCapture(e.pointerId); });
    canvas.addEventListener('pointermove', (e) => { if (dragX == null) return; spin += (e.clientX - dragX) * 0.012; dragX = e.clientX; });
    canvas.addEventListener('pointerup', () => { dragX = null; });
  }

  /** 미리보기 카피바라를 look대로 입힌다. */
  function dress(L: Look, t: number) {
    setFur(prev, L.fur);
    setSize(prev, L.size === 'baby' ? 0.5 : 1);
    if ((prev.itemKey ?? 'none') !== L.head) setHead(prev, L.head, t);
    if ((prev.faceKey ?? 'none') !== L.face) setFace(prev, L.face);
    if ((prev.handKey ?? 'none') !== L.hand) setHand(prev, L.hand, o.room(), scene);
  }

  const current = () => trying ?? o.look();
  const roomOk = (k: ItemId) => { const r = (ITEMS[k] as { room?: RoomId }).room; return !r || r === o.room(); };
  const itemsOf = (s: GTab): ItemId[] => (Object.keys(ITEMS) as ItemId[]).filter((k) => ITEMS[k].slot === s && roomOk(k));
  const tabsHere = (): GTab[] => (['head', 'face', 'hand', 'size', 'fur'] as GTab[]).filter((s) => s === 'size' || itemsOf(s).length > 0);
  /** 칸마다 지금 고른 값. */
  const keyIn = (L: Look, s: GTab): string => (s === 'size' ? (L.size === 'baby' ? 'baby' : 'normal') : String(L[s]));

  function render() {
    tabsEl.replaceChildren();
    for (const s of tabsHere()) {
      const b = el('button', 'btn' + (s === tab ? ' sel' : ''), TX.tabs[s]); b.type = 'button';
      b.addEventListener('click', () => { tab = s; render(); });
      tabsEl.append(b);
    }
    grid.replaceChildren();
    const L = current(), worn = o.look();
    const cells: { key: string; icon: string; name: string; leaf: number; swatch?: string }[] = [];
    if (tab === 'size') cells.push({ key: 'normal', icon: '🐾', name: TX.normal, leaf: 0 }, { key: 'baby', icon: iconOf('baby'), name: nameOf('baby'), leaf: ITEMS.baby.leaf });
    else {
      if (tab !== 'fur') cells.push({ key: 'none', icon: '◌', name: TX.none, leaf: 0 });
      for (const k of itemsOf(tab)) cells.push({ key: k, icon: tab === 'fur' ? '' : iconOf(k), name: tab === 'fur' ? FUR[k as FurTone].label : nameOf(k), leaf: ITEMS[k].leaf, swatch: tab === 'fur' ? FUR[k as FurTone].fur : undefined });
    }
    for (const c of cells) {
      const sel = keyIn(L, tab) === c.key, wearing = keyIn(worn, tab) === c.key;
      const b = el('button', 'it' + (sel ? ' sel' : '')); b.type = 'button';
      const ic = el('i', 'ic', c.icon); if (c.swatch) ic.style.background = c.swatch;
      b.append(ic, el('span', 'nm', c.name), el('small', c.leaf ? 'price' : 'own', wearing ? TX.wearing : c.leaf ? '🍃' + c.leaf : TX.owned));
      b.addEventListener('click', () => pick(c.key, c.leaf));
      grid.append(b);
    }
    foot.replaceChildren();
    tag.hidden = !trying;
    const paid = trying ? paidPick() : null;
    if (paid) {
      const no = el('button', 'btn wide', TX.notNow); no.type = 'button';
      no.addEventListener('click', () => { trying = null; render(); });
      const get = el('button', 'btn dark wide2', fill(TX.getIt, { n: paid.leaf })); get.type = 'button';
      get.addEventListener('click', () => o.onGet(paid.key, paid.leaf));
      foot.append(no, get);
    } else {
      const done = el('button', 'btn dark wide3', TX.done); done.type = 'button';
      done.addEventListener('click', () => G.close());
      foot.append(done);
    }
  }
  /** 입어 보는 중인 유료 소품 (지금 칸 기준). */
  function paidPick(): { key: ItemId; leaf: number } | null {
    const L = trying!, worn = o.look();
    for (const s of ['head', 'face', 'hand', 'size', 'fur'] as GTab[]) {
      const k = keyIn(L, s);
      if (k === keyIn(worn, s) || k === 'none' || k === 'normal') continue;
      const leaf = (ITEMS as Record<string, { leaf: number }>)[k]?.leaf ?? 0;
      if (leaf) return { key: k as ItemId, leaf };
    }
    return null;
  }
  function pick(key: string, leaf: number) {
    const L: Look = { ...current() };
    if (tab === 'size') L.size = key === 'baby' ? 'baby' : 'normal';
    else (L as unknown as Record<string, string>)[tab] = key;
    if (!leaf) {
      // 가진 것: 바로 입는다 (입어 보던 유료 소품은 그 칸만 남긴다)
      o.wear(tab, key);
      trying = paidPickOf(L) ? L : null;
    } else trying = L;
    render();
  }
  const paidPickOf = (L: Look) => { const save = trying; trying = L; const p = paidPick(); trying = save; return p; };

  x.addEventListener('click', () => G.close());
  const G: Grooming = {
    el: box,
    get isOpen() { return open; },
    open() {
      open = true; trying = null;
      if (!tabsHere().includes(tab)) tab = 'head';
      ensureRenderer();
      render();
    },
    close() { if (!open) return; open = false; trying = null; o.onClose(); },
    update(t, dt) {
      if (!open || !renderer) return;
      const w = prevBox.clientWidth, h = prevBox.clientHeight;
      if (w && (canvas.width !== Math.round(w * renderer.getPixelRatio()) || canvas.height !== Math.round(h * renderer.getPixelRatio()))) { renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix(); }
      dress(current(), t);
      prev.facing = prev.targetFacing = spin;
      pose(prev, t, dt, false, { room: o.room() });
      prev.c.root.position.set(0, 0, 0);
      const s = prev.size < 1 ? 0.62 : 1;
      camera.position.set(0.9 * s, 2.1 * s, 7.4 * s); camera.lookAt(0, 1.15 * s, 0);
      renderer.render(scene, camera);
    },
  };
  return G;
}
